/**
 * A small modal holding one short form: a heading, a sentence, the fields, then Cancel and the one
 * primary action. Enter submits. Used for creating a team, inviting, and renaming.
 */

import { Button, Modal } from '@heroui/react';
import type { ReactNode } from 'react';

import { ErrorNote } from '../ui.tsx';

export function FormModal({
  isOpen,
  onClose,
  title,
  blurb,
  children,
  submitLabel,
  canSubmit = true,
  busy,
  error,
  onSubmit,
  footer,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  blurb?: ReactNode;
  children?: ReactNode;
  submitLabel: string;
  canSubmit?: boolean;
  busy?: boolean;
  error?: string;
  onSubmit: () => void;
  /** Replaces Cancel and the primary action (a result shown once, with only Done). */
  footer?: ReactNode;
}) {
  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Modal.Backdrop isKeyboardDismissDisabled={busy}>
        <Modal.Container placement="center" size="sm">
          <Modal.Dialog aria-label={title}>
            <Modal.CloseTrigger />
            <form
              className="contents"
              onSubmit={(e) => {
                e.preventDefault();
                if (canSubmit && !busy) onSubmit();
              }}
            >
              <Modal.Header className="flex-col items-start gap-1">
                <Modal.Heading>{title}</Modal.Heading>
                {blurb ? <p className="text-sm text-muted">{blurb}</p> : null}
              </Modal.Header>
              {children || error ? (
                <Modal.Body className="grid gap-3">
                  {children}
                  <ErrorNote error={error} />
                </Modal.Body>
              ) : null}
              <Modal.Footer className="flex items-center justify-end gap-2">
                {footer ?? (
                  <>
                    <Button variant="tertiary" onPress={onClose} isDisabled={busy}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="primary" isPending={busy} isDisabled={!canSubmit}>
                      {submitLabel}
                    </Button>
                  </>
                )}
              </Modal.Footer>
            </form>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
